/**
 * Voting System Types
 * 
 * IP-gated voting system for articles (claps) and collections (sentiment)
 * Similar to Medium's clap system and CoinMarketCap's sentiment voting
 */

export type SentimentType = 'bullish' | 'bearish';

/**
 * Article Clap Vote
 * Stored in: article_votes/{articleId}/votes/{ipHash}
 */
export interface ArticleVote {
  articleId: string;
  ipHash: string; // SHA-256 hash of IP address for privacy
  claps: number; // Number of claps (1-50 per IP)
  createdAt: Date;
  updatedAt: Date;
  lastClapAt: Date;
}

/**
 * Collection Sentiment Vote
 * Stored in: collection_votes/{collectionSlug}/votes/{ipHash}
 */
export interface CollectionVote {
  collectionSlug: string;
  ipHash: string; // SHA-256 hash of IP address for privacy
  sentiment: SentimentType; // 'bullish' or 'bearish'
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Article Vote Aggregation
 * Stored in: article_vote_counts/{articleId}
 * Cached for fast reads
 */
export interface ArticleVoteCount {
  articleId: string;
  totalClaps: number;
  uniqueVoters: number;
  lastUpdated: Date;
  // Percentile data for display
  averageClaps: number; // Average claps per voter
}

/**
 * Collection Sentiment Aggregation
 * Stored in: collection_vote_counts/{collectionSlug}
 * Cached for fast reads
 */
export interface CollectionVoteCount {
  collectionSlug: string;
  bullishVotes: number;
  bearishVotes: number;
  totalVotes: number;
  bullishPercentage: number; // 0-100
  bearishPercentage: number; // 0-100
  lastUpdated: Date;
}

/**
 * IP Rate Limiting Entry
 * Stored in: vote_rate_limits/{ipHash}
 * Short TTL for memory efficiency
 */
export interface VoteRateLimit {
  ipHash: string;
  lastVoteAt: Date;
  votesInWindow: number; // Number of votes in current time window
  windowStart: Date;
  expiresAt: Date; // Auto-delete after 1 hour
}

/**
 * Vote Action Request (from client)
 */
export interface VoteArticleRequest {
  articleId: string;
  claps: number; // 1-50
  clientId?: string; // Browser fingerprint + UUID for better tracking
}

export interface VoteCollectionRequest {
  collectionSlug: string;
  sentiment: SentimentType;
  clientId?: string; // Browser fingerprint + UUID for better tracking
}

/**
 * Vote Action Response (to client)
 */
export interface VoteResponse {
  success: boolean;
  message?: string;
  data?: {
    // Article clap response
    totalClaps?: number;
    userClaps?: number;
    uniqueVoters?: number;
    // Collection sentiment response
    bullishVotes?: number;
    bearishVotes?: number;
    totalVotes?: number;
    bullishPercentage?: number;
    bearishPercentage?: number;
    userVote?: SentimentType;
    cooldownRemaining?: number; // Seconds until user can change vote on THIS collection
  };
  error?: string;
  rateLimitReset?: number; // Seconds until rate limit resets
}

/**
 * Constants for voting system
 */
export const VOTING_CONSTANTS = {
  // Article clapping
  MAX_CLAPS_PER_IP: 50, // Maximum claps per article per IP
  MIN_CLAPS: 1,
  MAX_CLAPS_PER_ACTION: 10, // Maximum claps in a single action
  CLAP_RATE_LIMIT_WINDOW: 60 * 1000, // 1 minute
  CLAP_RATE_LIMIT_MAX: 20, // Max 20 clap actions per minute
  
  // Collection sentiment
  SENTIMENT_COOLDOWN: 24 * 60 * 60 * 1000, // 24 hours between sentiment changes
  
  // Cache TTL
  COUNT_CACHE_TTL: 60 * 1000, // 1 minute cache for vote counts
  RATE_LIMIT_TTL: 60 * 60 * 1000, // 1 hour TTL for rate limit entries
  
  // Batch operations
  BATCH_UPDATE_THRESHOLD: 10, // Update aggregates every N votes
} as const;

